// React and related imports
import React, { useEffect } from 'react';

// Flowbite components
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  HR,
  Label,
  Modal,
  ToggleSwitch,
} from 'flowbite-react';

// React Icons
import { HiInformationCircle } from 'react-icons/hi';

// i18 hooks
import { useTranslation } from 'react-i18next';

// RTK Query Hooks
import {
  useCreateContentContextMutation,
  useUpdateContentContextMutation,
} from '../../redux/api/contentContextApi';

// Redux selectors, hooks, and actions
import { useAppDispatch } from '../../redux/hooks/useAppDispatch';
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import {
  clearAllValidationErrors,
  clearSelectedContentContext,
  clearValidationErrors,
  closeContentContextModal,
  setValidationErrors,
} from '../../redux/slices/contentContextSlice';
import {
  selectFormHasErrors,
  selectIsContentContextModalOpenedForAdd,
  selectSelectedContentContext,
  selectValidationErrors,
} from '../../redux/selectors/contentContextSelectors';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

// Types and Enums
import { Status } from '../../common/enums/Status';
import { IContentContext } from '../../common/interfaces/ContentContext.interface';
import { WeekDays } from '../../common/enums/WeekDays';

// Custom Components
import { PrayerTimeSettings } from './PrayerTimeSettings/PrayerTimeSettings';
import { DateRangePicker } from './DateRangePicker';
import { MultiLangContextInputs } from './MultiLangContextInputs';
import { DaysSelection } from './DaysSelection';
import { AlertContentVisibility } from '../Shared/AlertContentVisibility';

// Custom Hooks
import { useContentContextState } from '../../hooks/useContentContextState';
import { useFormChanges } from '../../hooks/useFormChanges';
import { usePreventUnload } from '../../hooks/usePreventUnload';
import { ContentContextTypeSwitch } from './ContentContextTypeSwitch';
import { usePagination } from '../../hooks/usePagination';

// Utils
import { checkFieldValidity } from '../../utils/checkFieldValidity';

export const ContentContextModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { setCurrentPage } = usePagination();

  const isDarkMode = useAppSelector(selectIsDarkMode);
  const hasErrors = useAppSelector(selectFormHasErrors);
  const formErrors = Object.values(
    useAppSelector(selectValidationErrors)
  ).flat();
  const selectedContext = useAppSelector(selectSelectedContentContext);
  const isModalForAdd = useAppSelector(selectIsContentContextModalOpenedForAdd);
  const buttonClass = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  useEffect(() => {
    if (isModalForAdd) {
      validateField('title');
      validateField('description');
      validateField('time');
      validateField('days');
    }
  }, [isModalForAdd]);

  const [updateContentContext, { isLoading }] =
    useUpdateContentContextMutation();

  const [createContentContext, { isLoading: creating }] =
    useCreateContentContextMutation();

  const { isModalOpen, contextState, updateField } = useContentContextState();

  const isLoadingState = isLoading || creating;

  const { hasChanges, hasChangesRef, resetInitialValues } =
    useFormChanges<IContentContext>({
      initialValues: selectedContext as IContentContext,
      currentValues: contextState!,
    });

  usePreventUnload({
    shouldPrevent: hasChanges && isModalOpen,
  });

  const onCloseModal = () => {
    if (hasChangesRef.current) {
      const shouldClose = window.confirm(
        t('contentContext.unsavedChangesCloseConfirm')
      );
      if (!shouldClose) return; // If user cancels, don't close the modal
    }

    dispatch(clearSelectedContentContext());
    dispatch(closeContentContextModal());
    dispatch(clearAllValidationErrors());
  };

  const validateField = (field: string, value: string | WeekDays[] = '') => {
    const errors = checkFieldValidity(field, value);
    if (errors.length > 0) {
      dispatch(setValidationErrors({ field, errors }));
    } else {
      dispatch(clearValidationErrors(field));
    }
  };

  const handleCreate = async () => {
    try {
      if (contextState) {
        await createContentContext({
          ...contextState,
          days: contextState.days.map((day) =>
            parseInt(day)
          ) as unknown as WeekDays[],
        }).unwrap();
        resetInitialValues(onCloseModal);
        setCurrentPage(1); // Reset to page 1 to see the newly added context
      }
    } catch (err) {
      // The toast middleware reports the failure; the modal stays open with the input.
    }
  };

  const handleSaveEdits = async () => {
    try {
      if (contextState && contextState.id) {
        await updateContentContext({
          id: contextState.id,
          updatedContent: {
            ...contextState,
            days: contextState.days.map((day) =>
              parseInt(day)
            ) as unknown as WeekDays[],
          },
        }).unwrap();
        resetInitialValues(onCloseModal);
        setCurrentPage(1); // Reset to page 1 after edit since the updated item will appear at the top (sorted by updatedAt desc)
      }
    } catch (err) {
      // The toast middleware reports the failure; the modal stays open with the input.
    }
  };

  return (
    <Modal
      dismissible
      show={isModalOpen}
      size="5xl"
      onClose={onCloseModal}
      className="dark:bg-primary-100"
    >
      <Modal.Header className="modal-header">
        <div className="flex items-center">
          <span className="me-3">
            {isModalForAdd
              ? t('contentContext.addContentContext')
              : t('contentContext.editContentContext')}
          </span>
          <Badge
            size="sm"
            className="cursor-default bg-gray-200 text-gray-800 group-hover:bg-gray-200 dark:bg-gray-200 dark:text-gray-800 dark:group-hover:bg-gray-200"
          >
            {t(`contentContextTypes.${contextState?.type}`)}
          </Badge>
        </div>
      </Modal.Header>

      {/* Passing Criteria Alert  */}
      {formErrors && formErrors.length > 0 && (
        <Alert
          className={`m-auto mt-3 w-11/12`}
          withBorderAccent
          additionalContent={
            <AlertContentVisibility t={t} errors={formErrors} />
          }
          color="warning"
          icon={HiInformationCircle}
        >
          <span className="me-1 ms-1 font-extrabold">{t('alert.info')}</span>
          {isModalForAdd
            ? t('alert.contentContextAddMessage')
            : t('alert.contentContextEditMessage')}
        </Alert>
      )}

      <Modal.Body className="space-y-5">
        {/* Multi-language input */}
        <MultiLangContextInputs
          t={t}
          contextState={contextState}
          updateField={updateField}
          validateField={validateField}
        />

        {/* Status toggle */}
        <div>
          <div className="mb-2 block">
            <Label htmlFor="status" value={t('contentContext.status')} />
          </div>
          <ToggleSwitch
            id="status"
            checked={contextState?.status === Status.ACTIVE}
            label={t(`status.${contextState?.status}`)}
            onChange={() =>
              updateField(
                'status',
                contextState?.status === Status.ACTIVE
                  ? Status.INACTIVE
                  : Status.ACTIVE
              )
            }
          />
        </div>

        {isModalForAdd && (
          <ContentContextTypeSwitch
            t={t}
            contextState={contextState}
            updateField={updateField}
          />
        )}

        <HR.Trimmed />
        {/* Prayer time settings */}
        <PrayerTimeSettings
          t={t}
          contextState={contextState}
          updateField={updateField}
          validateField={validateField}
        />

        <HR.Trimmed />

        {/* Is Forever checkbox */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="isForever"
            checked={contextState?.isForever || false}
            className="checkBox"
            onChange={(e) => {
              updateField('isForever', e.target.checked);
              updateField('startDate', null);
              updateField('endDate', null);
              validateField('startDate', 'valid');
              validateField('endDate', 'valid');
              if (!e.target.checked) {
                validateField('startDate');
                validateField('endDate');
              }
            }}
          />
          <Label htmlFor="isForever">{t('contentContext.isForever')}</Label>
        </div>

        {/* Date range picker */}
        {!contextState?.isForever && (
          <DateRangePicker
            t={t}
            contextState={contextState}
            updateField={updateField}
            validateField={validateField}
          />
        )}

        {/* Days selection */}
        <DaysSelection
          t={t}
          contextState={contextState}
          updateField={updateField}
          validateField={validateField}
        />
        {/* </div> */}
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          className={buttonClass}
          onClick={isModalForAdd ? handleCreate : handleSaveEdits}
          disabled={
            (!hasChanges && !isModalForAdd) || hasErrors || isLoadingState
          }
        >
          {t('contentContext.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
